import { css } from '@emotion/react'
import {
    CSSProperties,
    memo,
    ReactElement,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { VideoType } from '../../constants/enums'
import { VIDEO_CONTAINER_CLASS } from '../../constants/video'
import {
    setupSnapVideo,
    startVideo,
    unregisterSnapVideo,
} from '../../state/state.snapvideo'
import { aspectRatio } from '../../utilities/dimensions'
import { useUserSelector } from '../Hooks/useUser'
import PreviewPoster from './PreviewPosterImage/PreviewPoster'

const commonRootStyles = {
    justifyContent: 'center',
    position: 'relative',
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    width: '100%',
    // borderRadius: shape.borderRadius,
} as const
const rootNoBackgroundStyles = css({
    ...commonRootStyles,
})
const rootStyles = css({
    ...commonRootStyles,
    background: '#000',
})
const videoStyles = css({
    justifyContent: 'center',
    alignItems: 'center',
    // height: '100%',
    // width: '100%',
    transform: 'translate3d(0,0,0)',
    [`&.${VIDEO_CONTAINER_CLASS}`]: {
        // increase specificity to override the global .videoplayerjs styles (videoplayer.css)
        // without absolute positioning, there will be a flicker when the video mounts
        position: 'absolute',
    },
    '& video': {
        objectFit: 'cover',
    },
})
const snapshotStyles = css({
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    width: '100%',
    transform: 'translate3d(0,0,0)',
})

const videoLowIndex = {
    zIndex: -1,
    pointerEvents: 'none' as const,
    touchActions: 'none' as const,
}

type LiveSnapStreamProps = {
    id: number
    isAway: boolean
    isBroadcasting: boolean
    isClubShow: boolean
    isGroupShow: boolean
    isLounge: boolean
    isModel: boolean
    isNGVideo: boolean
    isOffline: boolean
    isPrivateShow: boolean
    isTruePrivate: boolean
    banned: boolean
    videoMuted: boolean
    phase: string
    server: string
    videoID: VideoType
    channel: number
    children?: ReactElement
    fallback?: boolean
    height?: number
    hideControls?: boolean
    volumeOnly?: boolean
    noBackground?: boolean
    staticImage?: boolean
    styleOverrides?: CSSProperties
    suppressVideo?: boolean
    width?: number
    isAnonymous?: boolean
    onPreviewLoaded?(event): void
    onVideoPlaying?(): void
    compact?: boolean
    onClick?(event: SyntheticEvent): void
}

const LiveSnapStream = memo((props: LiveSnapStreamProps) => {
    const {
        id,
        banned,
        height,
        width,
        videoID,
        isAway,
        isBroadcasting,
        isClubShow,
        isGroupShow,
        isLounge,

        isModel,
        isNGVideo,
        isOffline,
        isPrivateShow,
        isTruePrivate,
        videoMuted,

        phase,
        channel,
        server,
        children,
        compact,

        styleOverrides,

        fallback = false,
        staticImage = false,
        volumeOnly = false,
        hideControls = false,
        suppressVideo = false,
        noBackground = false,
        isAnonymous,
        onPreviewLoaded,
        onVideoPlaying,

        onClick,
    } = props

    const authTks = useUserSelector('me', 'auth')
    const sessionId = useUserSelector('me', 'session')

    const video = useRef<HTMLDivElement>(undefined)
    const [videoPlaying, setVideoPlaying] = useState(false)
    const [isFullscreen, setVideoFullscreen] = useState(false)

    const safeHeight = height > 0 ? height : 0
    const safeWidth = width > 0 ? width : 0
    const { height: imageHeight, width: imageWidth } = aspectRatio({
        height: safeHeight,
        width: safeWidth,
    })
    // const isLarge = imageWidth > 320 || imageHeight > 180
    const snapshotDimensionStyles = useMemo(
        () => ({ width: imageWidth, height: imageHeight }),
        [imageWidth, imageHeight],
    )

    const shouldStopPlaying =
        banned ||
        suppressVideo ||
        isTruePrivate ||
        isPrivateShow ||
        isClubShow ||
        isGroupShow ||
        isAway ||
        isOffline ||
        !isBroadcasting
    const isStaticImage =
        staticImage ||
        (!suppressVideo && !!videoPlaying) ||
        (!videoPlaying && !shouldStopPlaying)

    useEffect(() => {
        if (videoPlaying && (shouldStopPlaying || fallback)) {
            unregisterSnapVideo(id, videoID)
            setVideoPlaying(false)
        } else if (
            !videoPlaying &&
            video.current &&
            !(shouldStopPlaying || fallback)
        ) {
            if (
                setupSnapVideo(
                    id,
                    {
                        container: video.current,
                        hideControls,
                        forceMute: videoMuted,
                        onlyVolumeControls: volumeOnly,
                        videoID,
                    },
                    setVideoPlaying,
                    // handle video fullscreen toggle
                    () => {
                        const newFullscreen = !isFullscreen
                        const videoComponent =
                            video.current.querySelector('video')
                        if (newFullscreen) {
                            videoComponent.style.width = '100%'
                            videoComponent.style.height = '100%'
                        } else {
                            videoComponent.style.width = `${imageWidth}px !important`
                            videoComponent.style.height = `${imageHeight}px !important`
                        }
                        setVideoFullscreen(newFullscreen)
                    },
                )
            ) {
                startVideo(id)
            }
        }
    }, [
        video.current,
        sessionId,
        authTks?.ctxenc,
        shouldStopPlaying,
        suppressVideo,
        videoPlaying,
        isFullscreen,
        fallback,
    ])

    useEffect(() => {
        return () => {
            unregisterSnapVideo(id, videoID)
        }
    }, [id, videoID])

    useEffect(() => {
        if (typeof onVideoPlaying === 'function' && videoPlaying) {
            onVideoPlaying()
        }
    }, [videoPlaying, onVideoPlaying])

    const onPreviewLoadedCallback = useCallback(
        (event) => {
            if (typeof onPreviewLoaded === 'function') {
                onPreviewLoaded(event)
            }
        },
        [onPreviewLoaded],
    )

    if (fallback) {
        return children || null
    }

    const showVideo = videoPlaying && !shouldStopPlaying

    return (
        <div
            onClick={onClick}
            css={noBackground ? rootNoBackgroundStyles : rootStyles}
            style={styleOverrides}
            id={`snap-video-${id}`}
        >
            <div
                ref={video}
                className={VIDEO_CONTAINER_CLASS}
                data-testid="LiveSnapStream__Video"
                css={videoStyles}
                style={{
                    height: `${imageHeight}px`,
                    width: `${imageWidth}px`,
                }}
            />
            <div
                css={snapshotStyles}
                style={showVideo ? videoLowIndex : snapshotDimensionStyles}
            >
                <PreviewPoster
                    id={id}
                    isAnonymous={isAnonymous}
                    banned={banned}
                    channel={channel}
                    height={safeHeight}
                    isAway={isAway}
                    isBroadcasting={isBroadcasting}
                    isClubShow={isClubShow}
                    isGroupShow={isGroupShow}
                    isLounge={isLounge}
                    isModel={isModel}
                    isNGVideo={isNGVideo}
                    isOffline={isOffline}
                    isPrivateShow={isPrivateShow}
                    isTruePrivate={isTruePrivate}
                    large
                    noBackground={noBackground}
                    phase={phase}
                    server={server}
                    setDimensions
                    staticImage={isStaticImage || showVideo}
                    width={safeWidth}
                    onLoad={onPreviewLoadedCallback}
                    compact={compact}
                />
            </div>
        </div>
    )
})

export default LiveSnapStream
